import {
  type ExtractPropTypes,
  type PropType,
  computed,
  defineComponent,
  reactive,
  ref,
  withKeys,
} from 'vue'

// Utils
import {
  BORDER_LEFT,
  BORDER_LIGHTER,
  BORDER_TOP,
  type ComponentInstance,
  addUnit,
  callInterceptor,
  createNamespace,
  extend,
  isFunction,
  makeStringProp,
  noop,
  numericProp,
  pick,
  truthProp,
  unknownProp,
} from '@xzx-design/utils'

// Components
import { Button, ButtonGroup, Icon, Popup } from '@xzx-design/components'

import {
  popupSharedPropKeys,
  popupSharedProps,
} from '@xzx-design/components/popup/src/shared'

// Types
import { CheckOneFilled, CloseOneFilled } from '@xzx-design/icons-vue'
import type { ButtonGroupDirection, DialogType } from '@xzx-design/components'
import type {
  DialogAction,
  DialogMessage,
  DialogMessageAlign,
  DialogTheme,
} from './types'

const [name, bem, t] = createNamespace('dialog')

export const dialogProps = extend({}, popupSharedProps, {
  title: String,
  type: [String, Object] as PropType<DialogType>,
  theme: String as PropType<DialogTheme>,
  width: numericProp,
  message: [String, Function] as PropType<DialogMessage>,
  callback: Function as PropType<(action?: DialogAction) => void>,
  allowHtml: Boolean,
  className: unknownProp,
  transition: makeStringProp('xzx-dialog-bounce'),
  messageAlign: String as PropType<DialogMessageAlign>,
  closeOnPopstate: truthProp,
  showCancelButton: Boolean,
  cancelButtonText: String,
  cancelButtonColor: String,
  cancelButtonDisabled: Boolean,
  confirmButtonText: String,
  confirmButtonColor: String,
  confirmButtonDisabled: Boolean,
  showConfirmButton: truthProp,
  closeOnClickOverlay: Boolean,
  keyboardEnabled: truthProp,
  buttonDirection: makeStringProp<ButtonGroupDirection>('horizontal'),
})

export type DialogProps = ExtractPropTypes<typeof dialogProps>

const popupInheritKeys = [
  ...popupSharedPropKeys,
  'transition',
  'closeOnPopstate',
] as const

export default defineComponent({
  name,

  props: dialogProps,

  emits: ['confirm', 'cancel', 'keydown', 'update:show'],

  setup(props, { emit, slots }) {
    const root = ref<ComponentInstance>()
    const loading = reactive({
      confirm: false,
      cancel: false,
    })

    const updateShow = (value: boolean) => emit('update:show', value)

    const close = (action: DialogAction) => {
      updateShow(false)
      props.callback?.(action)
    }

    const getActionHandler = (action: DialogAction) => () => {
      // should not trigger close event when hidden
      if (!props.show) {
        return
      }

      emit(action)

      if (props.beforeClose) {
        loading[action] = true
        callInterceptor(props.beforeClose, {
          args: [action],
          done() {
            close(action)
            loading[action] = false
          },
          canceled() {
            loading[action] = false
          },
        })
      } else {
        close(action)
      }
    }

    const onCancel = getActionHandler('cancel')
    const onConfirm = getActionHandler('confirm')
    const onKeydown = withKeys(
      (event: KeyboardEvent) => {
        if (!props.keyboardEnabled) {
          return
        }
        // skip keyboard events of child elements
        if (event.target !== root.value?.popupRef?.value) {
          return
        }

        const onEventType: Record<string, () => void> = {
          Enter: props.showConfirmButton ? onConfirm : noop,
          Escape: props.showCancelButton ? onCancel : noop,
        }

        onEventType[event.key]()
        emit('keydown', event)
      },
      ['enter', 'esc']
    )

    const renderTypeIcon = (type: DialogType) => {
      const iconMap = {
        success: CheckOneFilled,
        fail: CloseOneFilled,
      }

      const iconColor = computed(() =>
        type === 'success'
          ? 'var(--xzx-color-success'
          : 'var(--xzx-color-danger)'
      )

      const typeIcon = computed(() => iconMap[type])
      return (
        <div class={bem('icon-type')}>
          <Icon color={iconColor.value}>
            <typeIcon.value />
          </Icon>
        </div>
      )
    }

    const renderTitle = () => {
      const { type } = props
      const title = slots.title ? slots.title() : props.title
      if (title) {
        return (
          <div
            class={bem('header', {
              isolated: !props.message && !slots.default,
            })}
          >
            {type && renderTypeIcon(type)}
            {title}
          </div>
        )
      }
    }

    const renderMessage = (hasTitle: boolean) => {
      const { message, allowHtml, messageAlign } = props
      const classNames = bem('message', {
        'has-title': hasTitle,
        [messageAlign as string]: messageAlign,
      })

      const content = isFunction(message) ? message() : message

      if (allowHtml && typeof content === 'string') {
        return <div class={classNames} innerHTML={content} />
      }

      return <div class={classNames}>{content}</div>
    }

    const renderContent = () => {
      if (slots.default) {
        return <div class={bem('content')}>{slots.default()}</div>
      }

      const { title, message, allowHtml } = props
      if (message) {
        const hasTitle = !!(title || slots.title)
        return (
          <div
            // add key to force re-render
            // see: https://github.com/vant-ui/vant/issues/7963
            key={allowHtml ? 1 : 0}
            class={bem('content', { isolated: !hasTitle })}
          >
            {renderMessage(hasTitle)}
          </div>
        )
      }
    }

    const renderTextButtons = () => {
      const { buttonDirection } = props

      const isVertical = buttonDirection === 'vertical'

      return (
        <div class={[BORDER_TOP, bem('footer'), bem('footer-text')]}>
          <ButtonGroup block direction={buttonDirection} reverse={isVertical}>
            {props.showCancelButton && (
              <Button
                size="large"
                text={props.cancelButtonText || t('cancel')}
                class={bem('cancel')}
                style={{ color: props.cancelButtonColor }}
                loading={loading.cancel}
                disabled={props.cancelButtonDisabled}
                onClick={onCancel}
              />
            )}
            {
              <i
                class={{
                  [isVertical ? BORDER_TOP : BORDER_LEFT]:
                    props.showCancelButton,
                  [BORDER_LIGHTER]: true,
                }}
              ></i>
            }
            {props.showConfirmButton && (
              <Button
                size="large"
                text={props.confirmButtonText || t('confirm')}
                class={[bem('confirm')]}
                style={{ color: props.confirmButtonColor }}
                loading={loading.confirm}
                disabled={props.confirmButtonDisabled}
                onClick={onConfirm}
              />
            )}
          </ButtonGroup>
        </div>
      )
    }

    const renderButtons = () => {
      const { buttonDirection } = props
      const isVertical = buttonDirection === 'vertical'

      return (
        <div class={[bem('footer')]}>
          <ButtonGroup block direction={buttonDirection} reverse={isVertical}>
            {props.showCancelButton && (
              <Button
                type="primary"
                text
                bg
                style={{ color: props.cancelButtonColor }}
                loading={loading.cancel}
                disabled={props.cancelButtonDisabled}
                onClick={onCancel}
              >
                {props.cancelButtonText || t('cancel')}
              </Button>
            )}
            {props.showConfirmButton && (
              <Button
                type="primary"
                style={{ color: props.confirmButtonColor }}
                loading={loading.confirm}
                disabled={props.confirmButtonDisabled}
                onClick={onConfirm}
              >
                {props.confirmButtonText || t('confirm')}
              </Button>
            )}
          </ButtonGroup>
        </div>
      )
    }

    // const renderRoundButtons = () => (
    //   <ActionBar class={bem('footer')}>
    //     {props.showCancelButton && (
    //       <ActionBarButton
    //         type="info"
    //         text={props.cancelButtonText || t('cancel')}
    //         class={bem('cancel')}
    //         color={props.cancelButtonColor}
    //         loading={loading.cancel}
    //         disabled={props.cancelButtonDisabled}
    //         onClick={onCancel}
    //       />
    //     )}
    //     {props.showConfirmButton && (
    //       <ActionBarButton
    //         type="primary"
    //         text={props.confirmButtonText || t('confirm')}
    //         class={bem('confirm')}
    //         color={props.confirmButtonColor}
    //         loading={loading.confirm}
    //         disabled={props.confirmButtonDisabled}
    //         onClick={onConfirm}
    //       />
    //     )}
    //   </ActionBar>
    // )

    const renderFooter = () => {
      if (slots.footer) {
        return slots.footer()
      }

      return props.theme === 'text-button'
        ? renderTextButtons()
        : renderButtons()
    }

    return () => {
      const { width, title, theme, message, className } = props
      return (
        <Popup
          ref={root}
          role="dialog"
          class={[bem([theme]), className]}
          style={{ width: addUnit(width) }}
          tabindex={0}
          aria-labelledby={title || message}
          onKeydown={onKeydown}
          onUpdate:show={updateShow}
          {...pick(props, popupInheritKeys)}
        >
          {renderTitle()}
          {renderContent()}
          {renderFooter()}
        </Popup>
      )
    }
  },
})
